import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  EventAvailable as EventIcon,
  ExpandLess,
  ExpandMore,
  NotificationsNone as BellIcon,
  EventNote as CalendarIcon,
} from "@material-ui/icons";

import TeacherService from "./Locality/Service/teacherService";
import PageTitle from "../../components/PageTitle/PageTitle";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS_BACK = 3;

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: 32,
  },
  headerCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    background: "linear-gradient(135deg, #f7faff 0%, #ffffff 55%, #f3f7fc 100%)",
  },
  title: {
    fontWeight: 800,
    fontSize: "1.35rem",
    color: "#0f2d4d",
    lineHeight: 1.25,
  },
  subtitle: {
    color: "#46566b",
    marginTop: 4,
    maxWidth: 520,
  },
  filtersRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginTop: theme.spacing(2),
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
  },
  countChip: {
    backgroundColor: "#e8f0fe",
    color: "#1769d6",
    fontWeight: 700,
  },
  listCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    padding: theme.spacing(2),
    minHeight: 220,
  },
  loadingBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: "#9aa6b6",
    gap: 12,
  },
  emptyBox: {
    textAlign: "center",
    padding: theme.spacing(5, 2),
    borderRadius: 14,
    border: "1px dashed #cbd5e1",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  },
  emptyIconWrap: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    margin: "0 auto 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#eef4ff",
    color: "#1769d6",
  },
  timeline: {
    position: "relative",
    paddingLeft: 8,
    [theme.breakpoints.up("sm")]: {
      paddingLeft: 12,
    },
  },
  item: {
    display: "flex",
    gap: 14,
    marginBottom: 14,
    "&:last-child": {
      marginBottom: 0,
    },
  },
  rail: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: 28,
    flexShrink: 0,
  },
  dot: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#e8f0fe",
    color: "#1769d6",
    border: "2px solid #ffffff",
    boxShadow: "0 0 0 1px #d7e4f7",
    zIndex: 1,
  },
  line: {
    flex: 1,
    width: 2,
    background: "#e6eaf0",
    marginTop: 4,
    minHeight: 24,
  },
  eventCard: {
    flex: 1,
    minWidth: 0,
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    background: "#ffffff",
    padding: theme.spacing(1.75, 2),
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      borderColor: "#c9daf5",
      boxShadow: "0 6px 18px rgba(15, 45, 77, 0.06)",
    },
  },
  eventTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
  },
  subject: {
    fontWeight: 800,
    color: "#0f2d4d",
    fontSize: "1rem",
    lineHeight: 1.35,
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  metaChip: {
    backgroundColor: "#f4f6fa",
    color: "#46566b",
    fontWeight: 600,
  },
  reminderChip: {
    backgroundColor: "#e6f7ed",
    color: "#1a9b54",
    fontWeight: 700,
  },
  message: {
    color: "#46566b",
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
    marginTop: 12,
    fontSize: 14,
  },
  expandLink: {
    marginTop: 10,
    color: "#1769d6",
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    fontSize: 13,
    userSelect: "none",
  },
  regards: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: "1px solid #eef2f7",
  },
}));

function getCurrentMonthNum() {
  return String(new Date().getMonth() + 1).padStart(2, "0");
}

function buildYearOptions() {
  return Array.from({ length: YEARS_BACK + 1 }, (_, i) => CURRENT_YEAR - i);
}

function buildMonthOptions(year) {
  return Array.from({ length: 12 }, (_, i) => {
    const value = String(i + 1).padStart(2, "0");
    const label = new Date(year, i, 1).toLocaleString(undefined, { month: "long" });
    return { value, label };
  });
}

function toMonthQuery(year, monthNum) {
  return `${year}-${monthNum}`;
}

function formatWhen(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isReminderItem(item) {
  const subject = String(item?.subject || "");
  return /^reminder:/i.test(subject) || subject.toLowerCase().includes("is tomorrow");
}

function previewMessage(message, max = 180) {
  const text = String(message || "");
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
}

export default function TeacherNotifications() {
  const classes = useStyles();

  const yearOptions = useMemo(() => buildYearOptions(), []);
  const [items, setItems] = useState([]);
  const [expandedIds, setExpandedIds] = useState({});
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedMonthNum, setSelectedMonthNum] = useState(getCurrentMonthNum);
  const [listLoading, setListLoading] = useState(false);

  const monthOptions = useMemo(
    () => buildMonthOptions(selectedYear),
    [selectedYear],
  );

  const monthQuery = useMemo(
    () => toMonthQuery(selectedYear, selectedMonthNum),
    [selectedYear, selectedMonthNum],
  );

  const selectedMonthLabel = useMemo(() => {
    const found = monthOptions.find((m) => m.value === selectedMonthNum);
    return found ? found.label : "";
  }, [monthOptions, selectedMonthNum]);

  const handleYearChange = (year) => {
    const y = Number(year);
    setSelectedYear(y);
    if (y === CURRENT_YEAR) {
      setSelectedMonthNum(getCurrentMonthNum());
    } else {
      setSelectedMonthNum((prev) => prev || "01");
    }
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    setListLoading(true);
    TeacherService.listNotifications({
      year: selectedYear,
      month: monthQuery,
    })
      .then((res) => {
        const data = res?.data || res;
        setItems(Array.isArray(data?.notifications) ? data.notifications : []);
      })
      .catch(() => {
        setItems([]);
      })
      .finally(() => setListLoading(false));
  }, [selectedYear, monthQuery]);

  return (
    <Container maxWidth="md" className={classes.root}>
      <PageTitle title="Event Reminders" />

      <Card elevation={0} className={classes.headerCard}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" style={{ gap: 12 }}>
          <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
            <Box className={classes.emptyIconWrap} style={{ margin: 0, width: 48, height: 48 }}>
              <CalendarIcon />
            </Box>
            <Box>
              <Typography className={classes.title}>Your event reminders</Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Day-before reminders for holidays and class events assigned to you.
                Newest first.
              </Typography>
            </Box>
          </Box>
          <Chip
            className={classes.countChip}
            label={`${items.length} ${items.length === 1 ? "reminder" : "reminders"}`}
          />
        </Box>

        <Box className={classes.filtersRow}>
          <Typography variant="body2" style={{ color: "#9aa6b6", fontWeight: 600 }}>
            Showing {selectedMonthLabel} {selectedYear}
          </Typography>
          <Box className={classes.filters}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
              <InputLabel id="teacher-reminder-year-label">Year</InputLabel>
              <Select
                labelId="teacher-reminder-year-label"
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                label="Year"
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
              <InputLabel id="teacher-reminder-month-label">Month</InputLabel>
              <Select
                labelId="teacher-reminder-month-label"
                value={selectedMonthNum}
                onChange={(e) => setSelectedMonthNum(e.target.value)}
                label="Month"
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Card>

      <Card elevation={0} className={classes.listCard}>
        {listLoading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress size={28} style={{ color: "#1769d6" }} />
            <Typography variant="body2">Loading reminders…</Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box className={classes.emptyBox}>
            <Box className={classes.emptyIconWrap}>
              <BellIcon fontSize="large" />
            </Box>
            <Typography variant="subtitle1" style={{ fontWeight: 800, color: "#0f2d4d" }}>
              No reminders this month
            </Typography>
            <Typography variant="body2" style={{ color: "#46566b", marginTop: 6, maxWidth: 360, marginLeft: "auto", marginRight: "auto" }}>
              When a holiday or class event is coming up tomorrow, it will show here.
              Try another month if you expect older reminders.
            </Typography>
          </Box>
        ) : (
          <Box className={classes.timeline}>
            {items.map((item, index) => {
              const isExpanded = Boolean(expandedIds[item._id]);
              const reminder = isReminderItem(item);
              const message = isExpanded
                ? String(item.message || "")
                : previewMessage(item.message);

              return (
                <Box key={item._id || index} className={classes.item}>
                  <Box className={classes.rail}>
                    <Box className={classes.dot}>
                      <EventIcon style={{ fontSize: 18 }} />
                    </Box>
                    {index < items.length - 1 && <Box className={classes.line} />}
                  </Box>

                  <Box className={classes.eventCard}>
                    <Box className={classes.eventTop}>
                      <Typography className={classes.subject}>
                        {item.subject || "Event reminder"}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpanded(item._id)}
                        style={{ color: "#0f2d4d" }}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>

                    <Box className={classes.metaRow}>
                      {reminder && (
                        <Chip size="small" className={classes.reminderChip} label="Tomorrow" />
                      )}
                      <Chip
                        size="small"
                        className={classes.metaChip}
                        label={formatWhen(item.createdAt) || "—"}
                      />
                    </Box>

                    <Typography className={classes.message}>{message}</Typography>

                    {String(item.message || "").length > 180 && (
                      <Typography
                        className={classes.expandLink}
                        onClick={() => toggleExpanded(item._id)}
                      >
                        {isExpanded ? "Show less" : "Read more"}
                      </Typography>
                    )}

                    {isExpanded && item.thanksAndRegards ? (
                      <Box className={classes.regards}>
                        <Typography variant="caption" style={{ color: "#9aa6b6", fontWeight: 700 }}>
                          From
                        </Typography>
                        <Typography variant="body2" style={{ color: "#0f2d4d", fontWeight: 800 }}>
                          {item.thanksAndRegards}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>
    </Container>
  );
}
